import { css } from '@emotion/react';
import { token } from '@atlaskit/tokens';
import { OpenLetter } from './OpenLetter';
import { BREAKPOINTS } from '@constants/breakpoints';
import { type FormInputs, NewsletterSignupForm } from './NewsletterSignupForm';
import { NewsletterFormValues, usePostNewsletterSignup } from '@hooks/index';
import { Environment } from '@components/community/constants';
export { Environment };

export const SUCCESS_MESSAGE = 'You’re on the list! Thanks for signing up.';
export const ERROR_MESSAGE = 'Something went wrong. Try again.';

/**
 * NewsletterSignup component renders a newsletter subscription form with an open letter icon,
 * heading, description, and a form for email collection. It handles form submission and displays
 * success/error messages accordingly.
 *
 * More information on how to use this component can be found in the [Newsletter Signup](https://hello.atlassian.net/wiki/spaces/conex/pages/5387991007/Newsletter+Run+book+and+General+Info) documentation.
 *
 * @param {Object} props - Component props
 * @param {Environment} [props.environment] - The environment context for the newsletter signup
 * @param {string} [props.formUrl] - Custom URL for form submission
 * @param {string} [props.atlassianId] - Atlassian ID of the user
 * @param {string} [props.anonymousId] - Anonymous identifier for tracking
 * @param {Object} [props.eventHandlers] - Object containing event handler callbacks
 * @param {Function} [props.eventHandlers.onSubmit] - Callback fired when form is submitted
 * @param {Function} [props.eventHandlers.onSuccess] - Callback fired on successful submission
 * @param {Function} [props.eventHandlers.onError] - Callback fired when an error occurs
 * @param {Function} [props.eventHandlers.onPrivacyPolicyClick] - Callback fired when privacy policy is clicked
 * @returns {React.ReactElement} Newsletter signup component
 *
 * @example
 * <NewsletterSignup
 *   environment="production"
 *   formUrl="https://api.example.com/newsletter"
 *   atlassianId="user123"
 *   eventHandlers={{
 *     onSuccess: () => console.log('Success'),
 *     onError: (error) => console.error(error)
 *   }}
 * />
 */

export interface Props extends Omit<NewsletterFormValues, 'email' | 'firstname' | 'lastname'> {
  environment?: Environment;
  formUrl?: string;
  eventHandlers?: {
    onSubmit?: () => void | Promise<void>;
    onSuccess?: () => void | Promise<void>;
    onError?: (error: any) => void | Promise<void>;
    onPrivacyPolicyClick?: () => void | Promise<void>;
  };
}

export const NewsletterSignup = ({ environment, formUrl, atlassianId, anonymousId, eventHandlers = {} }: Props) => {
  const { handleNewsletterSignup, error, success } = usePostNewsletterSignup({ environment, ...eventHandlers });
  const { onSubmit = () => null, onPrivacyPolicyClick } = eventHandlers;

  const handleSubmit = async ({ email }: FormInputs) => {
    onSubmit();
    await handleNewsletterSignup({ email, anonymousId, atlassianId }, formUrl);
  };

  const successMessage = success ? SUCCESS_MESSAGE : undefined;
  const responseMessage = error ? ERROR_MESSAGE : successMessage;

  return (
    <div css={signUpWrapper}>
      <div css={stack}>
        <OpenLetter />
        <h2 css={headingStyles}>
          Sign up for the community <span>newsletter</span>
        </h2>
        <p css={descriptionStyles}>Get the latest resources, tips, and trends to help you grow.</p>
      </div>
      <NewsletterSignupForm
        response={{ message: responseMessage, isError: Boolean(error) }}
        onSubmit={handleSubmit}
        onPrivacyPolicyClick={onPrivacyPolicyClick}
      />
    </div>
  );
};

const signUpWrapper = css({
  margin: '0 auto',
  maxWidth: '624px',
});

const stack = css({
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: token('space.100'),
});

const headingStyles = css({
  fontFamily: '"Charlie Display", sans-serif',
  color: token('color.text'),
  fontSize: '40px',
  fontWeight: 700,
  lineHeight: '48px',
  letterSpacing: '-0.12px',
  marginTop: 0,
  '& span': {
    display: 'block',
    [BREAKPOINTS.MOBILE.MEDIA_QUERY]: {
      display: 'inline-block',
    },
  },
  [BREAKPOINTS.TABLET.MEDIA_QUERY]: {
    fontSize: '32px',
  },
  [BREAKPOINTS.MOBILE.MEDIA_QUERY]: {
    width: '340px',
    fontSize: '32px',
  },
});

const descriptionStyles = css({
  fontFamily: 'Inter, sans-serif',
  color: token('color.text'),
  fontSize: '20px',
  fontWeight: '400',
  lineHeight: '32px',
  marginTop: 0,
  [BREAKPOINTS.TABLET.MEDIA_QUERY]: {
    fontSize: '20px',
  },
  [BREAKPOINTS.MOBILE.MEDIA_QUERY]: {
    fontSize: '16px',
    lineHeight: '24px',
  },
});
